import { BannerTypes } from '@gin-betting/banners';
import core, { App, Entities, GQL } from '@gin-betting/core';
import matchList, { MatchList } from '@gin-betting/match-list';
import { Record } from 'immutable';
import { partition } from 'lodash';

import { matchListRoutes, SidebarView } from 'app-constants';
import { State } from 'types';
import { isFavoritePage, removeLastSlash } from 'utils';
import type { TraverseStateKeys } from './constants';

type Locate<K extends Exclude<TraverseStateKeys, 'betsHistory'>> = (
    state: State[K],
    entitiesState?: Entities.EntitiesState
) => string[];

type Locators = {
    [P in Exclude<TraverseStateKeys, 'betsHistory'>]: Locate<P>;
};

const {
    utils: { getIdsFromBetslipOddId, getIdsFromBetOddId, isOneOfPages },
} = core;

const {
    constants: { MATCH_LIST_CATEGORY_RECORD_NAME },
} = matchList;

const [
    tournamentPages,
    matchListPages,
] = partition(matchListRoutes as App.Routes[], r =>
    r.includes(removeLastSlash(App.Routes.TOURNAMENT))
);

export const matchLocators: Locators = {
    betslip: state => {
        return state
            .get('betslipOdds')
            .keySeq()
            .map(id => getIdsFromBetslipOddId(id).matchId)
            .toArray();
    },
    match: (state, entitiesState) => {
        const slugOrId = state.get('sportEventId');
        const matchId = entitiesState?.get('slugs').get(slugOrId)?.matchId;

        return [matchId || slugOrId].filter(Boolean);
    },
    matchList: state => {
        // TODO: remove usage of history in locator. GINFE-2806
        if (
            !isOneOfPages(matchListPages, { exact: true }) &&
            !isOneOfPages(tournamentPages)
        ) {
            return [];
        }

        return state
            .toSeq()
            .valueSeq()
            .reduce<string[]>((acc, v) => {
                if (!Record.isRecord(v) || !isMatchListCategory(v)) return acc;

                return acc.concat(v.matchIds.toArray());
            }, []);
    },
    stream: state => {
        return state.get('matchIds').toArray();
    },
    favorite: state => {
        // TODO: remove usage of history in locator. GINFE-2806
        if (!isFavoritePage()) return [];

        return state.get('favoriteIds').toArray();
    },
    banners: state => {
        return state.get('zoneBanners').reduce<string[]>((acc, zone) => {
            const matchIds = zone.config
                .filter(
                    (config): config is BannerTypes.BannerSportEvent =>
                        config.type === GQL.BannerType.betting_sport_event
                )
                .map(config => config.id);

            return acc.concat(matchIds);
        }, []);
    },
};

export const betHistoryLocator = (state: State): string[] => {
    if (state.get('ui').get('sidebarView') !== SidebarView.bets) return [];

    return state
        .get('entities')
        .get('bets')
        .reduce<string[]>((acc, bet) => {
            return acc.concat(
                bet.odds.map(oddId => getIdsFromBetOddId(oddId).matchId)
            );
        }, []);
};

const isMatchListCategory = (
    maybeCategory: Record<any>
): maybeCategory is MatchList.MatchListCategory => {
    return (
        Record.getDescriptiveName(maybeCategory) ===
        MATCH_LIST_CATEGORY_RECORD_NAME
    );
};
